import type { GameObject, WorldType, ColliderConfig } from '../types';

const PLAYER_RADIUS = 0.3;
const MAX_STEP_HEIGHT = 1.0; // Maximum height the player can step up (generous for stairs)

/**
 * Get effective collider for an object based on its model
 */
export function getObjectCollider(obj: GameObject): ColliderConfig | null {
  // If explicitly defined, use that
  if (obj.collider) {
    if (obj.collider.type === 'none') return null;
    return obj.collider;
  }

  // Default colliders based on model type
  switch (obj.modelId) {
    case 'pillar':
      return { type: 'circle', size: [0.4, 0] };
    case 'tree':
      return { type: 'circle', size: [0.3, 0] };
    case 'animated_door':
    case 'door':
      return { type: 'box', size: [1.2, 0.4] };
    case 'wall':
      return { type: 'box', size: [obj.scale[0], obj.scale[2]] };
    case 'chest':
      return { type: 'box', size: [0.8, 0.5] };
    case 'sage':
    case 'npc':
      return { type: 'circle', size: [0.5, 0] };
    case 'block':
      // Only if it's tall enough to block
      if (obj.scale[1] > 0.3) {
        return { type: 'box', size: [obj.scale[0], obj.scale[2]] };
      }
      return null;
    case 'spectral_remnant':
      return { type: 'circle', size: [0.4, 0] };
    // Walkable surfaces - handled by platform system, no blocking collision
    case 'platform':
    case 'ramp':
    case 'stairs':
      return null; // Let the platform system handle these
    default:
      return null;
  }
}

/**
 * Check if a position collides with an object
 */
export function checkCollision(
  x: number,
  z: number,
  obj: GameObject,
  world: WorldType,
  openedDoors: Set<string>
): boolean {
  // Skip if object is not visible in current world
  if (obj.visibleInWorld !== 'both' && obj.visibleInWorld !== world) {
    return false;
  }

  // Special handling for animated doors - no collision when open
  if (obj.modelId === 'animated_door' && openedDoors.has(obj.id)) {
    return false;
  }

  const collider = getObjectCollider(obj);
  if (!collider) return false;

  const objX = obj.position[0] + (collider.offset?.[0] || 0);
  const objZ = obj.position[2] + (collider.offset?.[1] || 0);

  if (collider.type === 'circle') {
    const radius = collider.size?.[0] || 0.5;
    const dx = x - objX;
    const dz = z - objZ;
    const distance = Math.sqrt(dx * dx + dz * dz);
    return distance < radius + PLAYER_RADIUS;
  }

  if (collider.type === 'box') {
    const halfWidth = (collider.size?.[0] || 1) / 2 + PLAYER_RADIUS;
    const halfDepth = (collider.size?.[1] || 1) / 2 + PLAYER_RADIUS;

    // Account for rotation (simplified - only handles 90 degree rotations)
    const rotation = obj.rotation[1];
    const isRotated = Math.abs(Math.sin(rotation)) > 0.5;

    const effectiveHalfWidth = isRotated ? halfDepth : halfWidth;
    const effectiveHalfDepth = isRotated ? halfWidth : halfDepth;

    return (
      x > objX - effectiveHalfWidth &&
      x < objX + effectiveHalfWidth &&
      z > objZ - effectiveHalfDepth &&
      z < objZ + effectiveHalfDepth
    );
  }

  return false;
}

/**
 * Check if a move from current position to new position is valid
 */
export function canMoveTo(
  newX: number,
  newZ: number,
  objects: GameObject[],
  world: WorldType,
  openedDoors: Set<string>
): boolean {
  for (const obj of objects) {
    if (checkCollision(newX, newZ, obj, world, openedDoors)) {
      return false;
    }
  }
  return true;
}

/**
 * Get interaction distance for an object
 */
export function getInteractionDistance(obj: GameObject): number {
  const modelId = obj.modelId;

  // NPC models (starting with npc_) get larger interaction distance
  if (modelId.startsWith('npc_')) {
    return 2.0;
  }

  switch (modelId) {
    case 'sage':
    case 'npc':
    case 'spectral_remnant':
      return 1.5;
    case 'ancient_stone':
    case 'oracle_chevelu':
    case 'runic_sphere_portal':
    case 'suburban_house':
    case 'house':
    case 'building':
    case 'school':
    case 'dimensional_rift':
    case 'upside_down_gate':
      return 3.5; // Large objects/buildings need bigger interaction radius
    case 'chest':
    case 'crystal':
    case 'orb':
      return 1.0;
    case 'block':
    case 'switch':
      return 0.8;
    default:
      return 1.2;
  }
}

/**
 * Get position to walk to when interacting with an object
 */
export function getInteractionPosition(
  obj: GameObject,
  playerX: number,
  playerZ: number
): [number, number] {
  const distance = getInteractionDistance(obj);
  const objX = obj.position[0];
  const objZ = obj.position[2];

  // Direction from object to player
  const dx = playerX - objX;
  const dz = playerZ - objZ;
  const length = Math.sqrt(dx * dx + dz * dz);

  if (length < 0.1) {
    // Player is at object position, pick a direction
    return [objX + distance, objZ];
  }

  // Position at interaction distance from object, towards player
  return [
    objX + (dx / length) * distance,
    objZ + (dz / length) * distance,
  ];
}

/**
 * Check if an object is a walkable platform
 */
export function isWalkablePlatform(obj: GameObject): boolean {
  // Explicitly marked as walkable
  if (obj.properties?.walkable === true) return true;
  if (obj.properties?.walkable === false) return false;

  // Model types that are walkable by default
  const walkableModels = ['platform', 'ramp', 'stairs', 'bridge'];
  if (walkableModels.includes(obj.modelId)) return true;

  // Blocks are walkable if they're short (like platforms)
  if (obj.modelId === 'block' && obj.scale[1] <= 1.0) {
    return true;
  }

  return false;
}

/**
 * Check if a position is on top of a platform
 */
export function isOnPlatform(
  x: number,
  z: number,
  obj: GameObject,
  world: WorldType
): boolean {
  // Check world visibility
  if (obj.visibleInWorld !== 'both' && obj.visibleInWorld !== world) {
    return false;
  }

  if (!isWalkablePlatform(obj)) return false;

  const objX = obj.position[0];
  const objZ = obj.position[2];

  // Calculate bounds based on scale and rotation
  const rotation = obj.rotation[1];
  const isRotated = Math.abs(Math.sin(rotation)) > 0.5;

  const halfWidth = (isRotated ? obj.scale[2] : obj.scale[0]) / 2;
  const halfDepth = (isRotated ? obj.scale[0] : obj.scale[2]) / 2;

  // Check if position is within platform bounds
  return (
    x >= objX - halfWidth &&
    x <= objX + halfWidth &&
    z >= objZ - halfDepth &&
    z <= objZ + halfDepth
  );
}

/**
 * Get the ground height at a given position (accounting for platforms)
 */
export function getGroundHeight(
  x: number,
  z: number,
  currentY: number,
  objects: GameObject[],
  world: WorldType
): number {
  let groundHeight = 0; // Base floor height

  for (const obj of objects) {
    if (!isOnPlatform(x, z, obj, world)) continue;

    // Calculate platform top height
    const platformHeight = obj.position[1] + obj.scale[1];

    // Check if this platform is reachable from current height
    const heightDiff = platformHeight - currentY;

    // Can step up onto platforms within MAX_STEP_HEIGHT
    // Can always step down
    if (heightDiff <= MAX_STEP_HEIGHT) {
      groundHeight = Math.max(groundHeight, platformHeight);
    }
  }

  return groundHeight;
}

/**
 * Check if a move is valid considering platforms
 * Returns [canMove, newY] where newY is the ground height at the new position
 */
export function canMoveToWithHeight(
  newX: number,
  newZ: number,
  currentY: number,
  objects: GameObject[],
  world: WorldType,
  openedDoors: Set<string>
): [boolean, number] {
  // First check standard collision
  for (const obj of objects) {
    // Walkable platforms NEVER block collision - player walks ON them
    if (isWalkablePlatform(obj)) {
      continue;
    }

    if (checkCollision(newX, newZ, obj, world, openedDoors)) {
      return [false, currentY];
    }
  }

  // Get ground height at new position
  const newGroundHeight = getGroundHeight(newX, newZ, currentY, objects, world);

  return [true, newGroundHeight];
}
